import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  X,
  Search,
  Plus,
  Image as ImageIcon,
  Play,
  Check
} from 'lucide-react';
import { galleryService } from '@/services/galleryService';
import { isVideoUrl } from '../../utils/imageUrl';
import { Button } from '@/components/ui/Button/Button';
import { 
  GalleryOptionsParams 
} from '@/types/gallery';

interface AssetSelectorProps {
  galleryId: number;
  onClose: () => void;
  onAssetsAdded: () => void;
}

export const AssetSelector: React.FC<AssetSelectorProps> = ({
  galleryId,
  onClose,
  onAssetsAdded
}) => {
  const [selectedAssets, setSelectedAssets] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  // Build query parameters
  const queryParams: GalleryOptionsParams = {
    page: currentPage,
    page_size: 20,
  };

  // Fetch available assets
  const {
    data: assetsResponse,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['gallery-options', galleryId, queryParams],
    queryFn: () => galleryService.getGalleryOptions(galleryId, queryParams),
    placeholderData: (previousData) => previousData,
  });

  // Add assets mutation
  const addAssetsMutation = useMutation({
    mutationFn: (assetLibraryIds: number[]) => 
      galleryService.addAssetsToGallery(galleryId, assetLibraryIds),
    onSuccess: () => {
      onAssetsAdded();
    }
  });

  const assets = assetsResponse?.data?.list || [];
  const pageInfo = assetsResponse?.data?.pageInfo;

  const handleAssetToggle = (assetId: number) => {
    const newSelected = new Set(selectedAssets);
    if (newSelected.has(assetId)) {
      newSelected.delete(assetId);
    } else {
      newSelected.add(assetId);
    }
    setSelectedAssets(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedAssets.size === assets.length) {
      setSelectedAssets(new Set());
    } else {
      setSelectedAssets(new Set(assets.map(asset => asset.id)));
    }
  };

  const handleAddAssets = () => {
    if (selectedAssets.size === 0) return;
    
    addAssetsMutation.mutate(Array.from(selectedAssets));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Note: Search functionality would need to be implemented in the API
    // For now, this is a placeholder
    setCurrentPage(1);
    refetch();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-rgf-gray-500 bg-opacity-75" onClick={onClose} />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-rgf-gray-900">
                Add Assets to Gallery
              </h3>
              <p className="mt-1 text-sm text-rgf-gray-500">
                Select photos and videos from the asset library to add to this gallery
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              icon={<X className="h-4 w-4" />}
            />
          </div>

          {/* Search and Actions */}
          <div className="flex items-center justify-between mb-6">
            <form onSubmit={handleSearch} className="flex flex-1 max-w-lg">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-rgf-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search assets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-rgf-primary-500 focus:border-rgf-primary-500 text-sm"
                />
              </div>
              <Button type="submit" className="ml-2" size="sm">
                Search
              </Button>
            </form>

            <div className="flex items-center space-x-3">
              {assets.length > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSelectAll}
                >
                  {selectedAssets.size === assets.length ? 'Deselect All' : 'Select All'}
                </Button>
              )}
              
              <Button
                onClick={handleAddAssets}
                disabled={selectedAssets.size === 0}
                loading={addAssetsMutation.isPending}
                icon={<Plus className="h-4 w-4" />}
              >
                Add {selectedAssets.size} Asset{selectedAssets.size !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="min-h-96">
            {isLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="aspect-square bg-rgf-gray-200 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-red-600 mb-4">Error loading assets: {error.message}</p>
                  <Button variant="secondary" onClick={() => refetch()}>
                    Retry
                  </Button>
                </div>
              </div>
            ) : assets.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <ImageIcon className="mx-auto h-12 w-12 text-rgf-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-rgf-gray-900">No assets available</h3>
                  <p className="mt-1 text-sm text-rgf-gray-500">
                    No assets found for this gallery's location and program, or all available assets have already been added.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {assets.map((asset) => {
                  const isSelected = selectedAssets.has(asset.id);
                  
                  return (
                    <div
                      key={asset.id}
                      className={`relative cursor-pointer rounded-lg overflow-hidden transition-all duration-200 ${
                        isSelected 
                          ? 'ring-2 ring-rgf-primary-500 shadow-lg' 
                          : 'hover:shadow-md border border-rgf-gray-200'
                      }`}
                      onClick={() => handleAssetToggle(asset.id)}
                    >
                      <div className="aspect-square relative">
                        {isVideoUrl(asset.cdn_url) ? (
                          <>
                            <video
                              src={asset.cdn_url}
                              muted
                              playsInline
                              preload="metadata"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLVideoElement).style.display = 'none';
                              }}
                            />
                            <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white px-1.5 py-0.5 rounded text-xs flex items-center">
                              <Play className="h-3 w-3 mr-0.5" />Video
                            </div>
                          </>
                        ) : (
                          <img
                            src={asset.cdn_url}
                            alt={asset.file_name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              if (!img.dataset.fallback) {
                                img.dataset.fallback = '1';
                                img.src = '/placeholder-image.svg';
                              }
                            }}
                          />
                        )}
                      </div>
                      
                      {/* Selection indicator */}
                      <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 ${
                        isSelected 
                          ? 'bg-rgf-primary-500 text-white' 
                          : 'bg-white bg-opacity-80 text-rgf-gray-400 hover:bg-opacity-100'
                      }`}>
                        {isSelected ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                      </div>

                      {/* File name overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-2">
                        <p className="text-xs truncate" title={asset.file_name}>
                          {asset.file_name}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pagination */}
          {pageInfo && pageInfo.totalRows > pageInfo.pageSize && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-rgf-gray-200">
              <div className="text-sm text-rgf-gray-700">
                Showing {(pageInfo.page - 1) * pageInfo.pageSize + 1} to{' '}
                {Math.min(pageInfo.page * pageInfo.pageSize, pageInfo.totalRows)} of{' '}
                {pageInfo.totalRows} assets
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={pageInfo.isFirstPage}
                >
                  Previous
                </Button>
                
                <span className="text-sm text-rgf-gray-700">
                  Page {pageInfo.page}
                </span>
                
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={pageInfo.isLastPage}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};